// src/services/task.service.ts
import { Task } from "../types/task";
import { TaskStatus, TaskPriority } from "../types/enum"; // Import TaskPriority
import { authHeader } from "./api";
import { MessageResponse } from "../types/response";

// Define the structure of the Page object received from the backend
interface Page<T> {
  content: T[];
  totalPages: number;
  totalElements: number;
  size: number;
  number: number;
  // Add other properties from the Page object if you plan to use them for pagination UI
  // E.g., first: boolean; last: boolean; numberOfElements: number; empty: boolean;
}

// const API_BASE_URL = "http://localhost:8080/api/tasks";
const API_BASE_URL =
  "https://task-management-backend-1g4v.onrender.com/api/tasks";

// Helper to handle API responses and parse errors
// Changed return type to Promise<any> to reflect that it can return different structures
const handleResponse = async (response: Response): Promise<any> => {
  if (!response.ok) {
    const errorResponse = response.clone();
    let errorData: any;
    try {
      errorData = await errorResponse.json();
    } catch (e) {
      errorData = {
        message: `HTTP error! Status: ${response.status} - ${
          response.statusText || "Unknown Error"
        }`,
      };
      if (response.status === 401 || response.status === 403) {
        errorData.message = "Authentication required or forbidden.";
      }
    }
    throw new Error(
      errorData.message || `HTTP error! status: ${response.status}`
    );
  }
  return response.json();
};

// Updated return type to Promise<Page<Task>>
const getAllTasks = async (
  status?: TaskStatus,
  priority?: TaskPriority, // New optional parameter
  keyword?: string, // New optional parameter for search
  page: number = 0, // Default to page 0
  size: number = 5, // Default to size 5 (or whatever your backend default is)
  sortBy?: string,
  sortDir?: "asc" | "desc"
): Promise<Page<Task>> => {
  // <--- IMPORTANT CHANGE HERE
  const params = new URLSearchParams();
  if (status) params.append("status", status);
  if (priority) params.append("priority", priority); // Add priority to params
  if (keyword) params.append("keyword", keyword); // Add keyword to params
  params.append("page", page.toString()); // Always send page
  params.append("size", size.toString()); // Always send size
  if (sortBy && sortDir) params.append("sort", `${sortBy},${sortDir}`);

  const url = `${API_BASE_URL}?${params.toString()}`;

  const response = await fetch(url, { headers: authHeader() });
  return handleResponse(response); // This will return the full Page object
};

const getTaskById = async (id: number): Promise<Task> => {
  const response = await fetch(`${API_BASE_URL}/${id}`, {
    headers: authHeader(),
  });
  return handleResponse(response);
};

const createTask = async (
  task: Omit<Task, "id" | "createdAt" | "updatedAt" | "userId">
): Promise<Task> => {
  const response = await fetch(API_BASE_URL, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...authHeader(),
    },
    body: JSON.stringify(task),
  });
  return handleResponse(response);
};

const updateTask = async (
  id: number,
  task: Omit<Task, "id" | "createdAt" | "updatedAt" | "userId">
): Promise<Task> => {
  const response = await fetch(`${API_BASE_URL}/${id}`, {
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
      ...authHeader(),
    },
    body: JSON.stringify(task),
  });
  return handleResponse(response);
};

const deleteTask = async (id: number): Promise<MessageResponse> => {
  const response = await fetch(`${API_BASE_URL}/${id}`, {
    method: "DELETE", // <--- ADD THIS LINE!
    headers: authHeader(),
  });
  if (!response.ok) {
    // If the response is not OK, throw an error using your helper
    // This will catch 4xx, 5xx responses, etc.
    throw new Error(
      (await response.json()).message ||
        `Failed to delete task: ${response.statusText}`
    );
  }
  // For successful deletion (HTTP 204 No Content), return the success message
  // Otherwise, handle response if backend sends content on success (less common for DELETE)
  return { message: "Task deleted successfully" };
};

const TaskService = {
  getAllTasks,
  getTaskById,
  createTask,
  updateTask,
  deleteTask,
};

export default TaskService;
